import clientRequestSchema from "../../../schemas/explorer-client-request.v1.schema.json";
import errorCodesSchema from "../../../schemas/error-codes.v1.schema.json";
import explorerSchema from "../../../schemas/explorer.v1.schema.json";
import responseSchema from "../../../schemas/explorer-response.v1.schema.json";
import { createRuntimeBoundaryValidator } from "../../../packages/contracts/src/runtime-validation.mjs";
import { createServerProfileTransport } from "../../../packages/explorer-state/src/http-transport.mjs";
import { ApiError, type ApiDispatcher } from "./api";
import type {
  ApiMeta,
  ApiSuccess,
  Bootstrap,
  CacheSnapshot,
  ConsistencyMode,
  DeploymentIdentityWarning,
  EaclObject,
  KnownSubjectPage,
  ObjectPage,
  PermissionDecision,
  ReaderHealth,
  RelationshipPage,
  ResourceCount,
  SchemaInfo,
  SeedProgress,
} from "./types";

export interface ExplorerProfile {
  id: string;
  backend: string;
  storage: string;
  state: "enabled";
  reason: null;
  route: string;
  apiOrigin: string | null;
  deployment: {
    demoSha: string;
    eaclSha: string;
    artifact: {
      kind: "static" | "lambda-version";
      sha256: string;
      version: string;
    };
    deploymentId: string;
    dataManifestSha256: string;
    deployedAt: string;
  };
}

interface ProfileDescriptor {
  identity: {
    profileId: string;
    demoSha: string;
    eaclSha: string;
    artifactSha256: string;
    deploymentId: string;
    dataManifestSha256: string;
  };
  identityWarning?: DeploymentIdentityWarning;
  profile: { backend: string; storage: string };
  capabilities: {
    operations: string[];
    consistencyModes: string[];
    snapshotBehavior: string;
    cacheBehavior: string;
    limitations: string[];
  };
  dataset: { logicalResourceCount: number; serverCount: number };
  localSeed?: { modified: boolean; progress: LocalSeedProgress };
  basis: { id: string; capturedAt: string; behavior: string; fixedForEnvironment: boolean };
}

interface LocalSeedProgress {
  status: "ready" | "seeding" | "error";
  resourcesAdded: number;
  resourcesCompleted: number;
  resourcesTarget: number;
  totalResources: number;
  totalServers: number;
  error?: string;
}

function presentLocalSeed(progress: LocalSeedProgress): SeedProgress {
  return {
    status: progress.status,
    serversAdded: progress.resourcesAdded,
    serversCompleted: progress.resourcesCompleted,
    serversTarget: progress.resourcesTarget,
    totalServers: progress.totalServers,
    totalResources: progress.totalResources,
    unit: "resources",
    error: progress.error,
    label: "Adding browser-local resources",
  };
}

interface WireEnvelope<T> {
  data?: T;
  error?: { code: string; message: string };
  meta: {
    requestId: string;
    revision: string;
    elapsedMs?: number;
    cacheStatus?: ApiMeta["cacheStatus"];
  };
}

export interface ExplorerTransport {
  bootstrap: (options?: { signal?: AbortSignal | null }) => Promise<unknown>;
  request: (
    operation: string,
    input?: Record<string, unknown>,
    options?: { signal?: AbortSignal | null },
  ) => Promise<unknown>;
  release: () => Promise<unknown>;
}

interface WireObject {
  type: string;
  id: string;
}

interface WirePage<T> {
  items: T[];
  pageInfo: { hasNextPage: boolean; endCursor: string | null; pageSize: number };
}

interface WireSchema {
  sha256: string;
  types: Array<{
    name: string;
    relations: Array<{ name: string; subjectTypes: string[] }>;
    permissions: Array<{ name: string; expression: string }>;
  }>;
}

const schemas = { clientRequestSchema, errorCodesSchema, explorerSchema, responseSchema };
const validateRequest = createRuntimeBoundaryValidator(
  schemas,
  "https://demo.eacl.dev/schemas/explorer-client-request.v1.schema.json",
  "serverClientRequest",
);
const validateResponse = createRuntimeBoundaryValidator(
  schemas,
  "https://demo.eacl.dev/schemas/explorer-response.v1.schema.json",
  "serverResponse",
);

export function createProfileApi(
  profile: ExplorerProfile,
  options: { transport?: ExplorerTransport; sequential?: boolean } = {},
): {
  dispatcher: ApiDispatcher;
  release: () => Promise<void>;
} {
  const transport = options.transport ?? createServerProfileTransport({
    profile,
    validateRequest,
    validateResponse,
    sequential: options.sequential === true,
  });
  let descriptor: ProfileDescriptor | undefined;
  let schema: SchemaInfo | undefined;
  const subjectCursors = new Map<string, Map<number, string | null>>();

  const wire = async <T>(
    operation: string,
    input: Record<string, unknown>,
    signal?: AbortSignal | null,
  ): Promise<WireEnvelope<T>> => {
    const result = (await transport.request(operation, input, { signal })) as WireEnvelope<T>;
    if (result.error || result.data === undefined) {
      throw new ApiError(400, {
        error: {
          code: result.error?.code ?? "unexpected-api-response",
          message: result.error?.message ?? "The profile request failed.",
        },
      });
    }
    return result;
  };

  const loadDescriptor = async (
    signal?: AbortSignal | null,
    refresh = false,
  ): Promise<ProfileDescriptor> => {
    if (!descriptor || refresh) {
      descriptor = (await transport.bootstrap({ signal })) as ProfileDescriptor;
      schema = undefined;
      subjectCursors.clear();
    }
    return descriptor;
  };

  const loadSchema = async (signal?: AbortSignal | null): Promise<SchemaInfo> => {
    if (schema) return schema;
    const active = await loadDescriptor(signal);
    const result = await wire<WireSchema>("get-schema", {
      consistency: preferredConsistency(active, "minimize-latency"),
    }, signal);
    schema = presentSchema(result.data!);
    return schema;
  };

  const envelope = <T>(
    data: T,
    requestId: string,
    basis: ProfileDescriptor["basis"] | null | undefined,
    elapsedMs?: number,
    cacheStatus?: ApiMeta["cacheStatus"],
  ): ApiSuccess<T> => ({
    data,
    meta: presentMeta(profile, requestId, basis, elapsedMs, cacheStatus),
  });
  const wireEnvelope = <T, U>(data: T, result: WireEnvelope<U>): ApiSuccess<T> =>
    ({
      data,
      meta: {
        revision: result.meta.revision,
        requestId: result.meta.requestId,
        ...(result.meta.elapsedMs === undefined ? {} : { elapsedMs: result.meta.elapsedMs }),
        ...(result.meta.cacheStatus === undefined ? {} : { cacheStatus: result.meta.cacheStatus }),
      },
    });

  const dispatcher: ApiDispatcher = async <T>(
    path: string,
    options: RequestInit = {},
  ): Promise<ApiSuccess<T>> => {
    const signal = options.signal;
    const url = new URL(path, window.location.origin);
    const body = parseBody(options.body);

    if (url.pathname === "/health") {
      const active = await loadDescriptor(signal);
      return envelope({
        status: "ready",
        ready: true,
        identity: active.identity,
        ...(active.identityWarning ? { identityWarning: active.identityWarning } : {}),
        basis: active.basis,
      } as ReaderHealth, "bootstrap-health", active.basis) as ApiSuccess<T>;
    }

    if (url.pathname === "/bootstrap" || url.pathname === "/refresh-snapshot") {
      const active = await loadDescriptor(signal, url.pathname === "/refresh-snapshot");
      const activeSchema = await loadSchema(signal);
      return envelope(presentBootstrap(active, activeSchema), "bootstrap", active.basis) as ApiSuccess<T>;
    }

    const active = await loadDescriptor(signal);
    if (url.pathname === "/seed") {
      if (profile.backend !== "datascript" || !active.localSeed) {
        throw new ApiError(403, { error: { code: "unsupported-operation", message: "This profile is read-only." } });
      }
      const operation = options.method === "POST" ? body.retry === true ? "seed-retry" : "seed-start" : "seed-status";
      const result = await wire<LocalSeedProgress>(operation,
        operation === "seed-start" ? { resourceCount: body.resourceCount } : {}, signal);
      // A following bootstrap must read the current local basis and counts.
      if (result.data!.status !== "seeding") {
        descriptor = undefined;
        subjectCursors.clear();
      }
      return wireEnvelope(presentLocalSeed(result.data!), result) as ApiSuccess<T>;
    }
    const selectedConsistency = consistencyMode(body.consistency);
    const exactSnapshotInput = selectedConsistency === "at-exact-snapshot"
      && active.capabilities.consistencyModes.includes("historical-date")
      ? exactSnapshot(body.consistency)
      : {};
    const consistency = preferredConsistency(
      active,
      selectedConsistency,
      "atExactSnapshotAt" in exactSnapshotInput,
    );
    const consistencyInput = {
      consistency,
      ...(selectedConsistency === "at-least-as-fresh"
        ? freshnessFloor(body.consistency)
        : {}),
      ...exactSnapshotInput,
    };

    if (url.pathname === "/get-schema") {
      const activeSchema = await loadSchema(signal);
      return envelope(activeSchema, "schema", active.basis) as ApiSuccess<T>;
    }

    if (url.pathname === "/count-objects") {
      const result = await wire<{value:number; exact:boolean; estimatedTotal?:number}>("count-objects", {kind:"relationships", ceiling:1000000}, signal);
      return wireEnvelope(result.data!, result) as ApiSuccess<T>;
    }

    if (url.pathname === "/get-cache-info") {
      const result = await wire<Record<string, unknown>>("get-cache-info", {}, signal);
      const snapshot = presentCacheSnapshot(result.data!);
      return wireEnvelope(snapshot, result) as ApiSuccess<T>;
    }

    if (url.pathname === "/list-subjects") {
      const offset = positiveInteger(url.searchParams.get("offset"), 0);
      const limit = positiveInteger(url.searchParams.get("limit"), 20);
      const cursors = subjectCursors.get(String(limit)) ?? new Map<number, string | null>([[0, null]]);
      subjectCursors.set(String(limit), cursors);
      let cursor = cursors.get(offset);
      if (cursor === undefined) {
        throw new ApiError(400, { error: { code: "invalid-cursor", message: "Return to the first subjects page." } });
      }
      const result = await wire<WirePage<WireObject>>("list-subjects", {
        type: "user",
        pageSize: limit,
        ...(cursor ? { cursor } : {}),
      }, signal);
      const nextOffset = offset + result.data!.items.length;
      if (result.data!.pageInfo.hasNextPage && result.data!.pageInfo.endCursor) {
        cursors.set(nextOffset, result.data!.pageInfo.endCursor);
      }
      const page: KnownSubjectPage = {
        data: result.data!.items.map(object),
        pageInfo: {
          hasNextPage: result.data!.pageInfo.hasNextPage,
          hasPreviousPage: offset > 0,
          offset,
          nextOffset: result.data!.pageInfo.hasNextPage ? nextOffset : null,
        },
      };
      return wireEnvelope(page, result) as ApiSuccess<T>;
    }

    if (url.pathname === "/lookup-resources") {
      const result = await wire<WirePage<WireObject>>("lookup-resources", {
        subjectType: nestedIdentifier(body, "subject", "type"),
        subjectId: nestedIdentifier(body, "subject", "id"),
        resourceType: identifier(body.resourceType),
        permission: identifier(body.permission),
        pageSize: number(body.pageSize, 20),
        cache: body.cache !== false,
        populateCache: body.populateCache !== false,
        ...(body.after ? { cursor: identifier(body.after) } : {}),
        ...consistencyInput,
      }, signal);
      return wireEnvelope(objectPage(result.data!), result) as ApiSuccess<T>;
    }

    if (url.pathname === "/count-resources") {
      const result = await wire<{ value: number; exact: boolean; ceiling: number }>("count-resources", {
        subjectType: nestedIdentifier(body, "subject", "type"),
        subjectId: nestedIdentifier(body, "subject", "id"),
        resourceType: identifier(body.resourceType),
        permission: identifier(body.permission),
        ceiling: number(body.countLimit, 1_000),
        cache: body.cache !== false,
        populateCache: body.populateCache !== false,
        ...consistencyInput,
      }, signal);
      const count: ResourceCount = {
        count: result.data!.value,
        limit: result.data!.ceiling,
        truncated: !result.data!.exact,
      };
      return wireEnvelope(count, result) as ApiSuccess<T>;
    }

    if (url.pathname === "/lookup-subjects") {
      const result = await wire<WirePage<WireObject>>("lookup-subjects", {
        resourceType: nestedIdentifier(body, "resource", "type"),
        resourceId: nestedIdentifier(body, "resource", "id"),
        subjectType: identifier(body.subjectType),
        permission: identifier(body.permission),
        pageSize: number(body.pageSize, 20),
        cache: body.cache !== false,
        populateCache: body.populateCache !== false,
        ...(body.after ? { cursor: identifier(body.after) } : {}),
        ...consistencyInput,
      }, signal);
      return wireEnvelope(objectPage(result.data!), result) as ApiSuccess<T>;
    }

    if (url.pathname === "/check-permission") {
      const result = await wire<{ allowed: boolean }>("check-permission", {
        subjectType: nestedIdentifier(body, "subject", "type"),
        subjectId: nestedIdentifier(body, "subject", "id"),
        resourceType: nestedIdentifier(body, "resource", "type"),
        resourceId: nestedIdentifier(body, "resource", "id"),
        permission: identifier(body.permission),
        cache: body.cache !== false,
        populateCache: body.populateCache !== false,
        ...consistencyInput,
      }, signal);
      return {
        data: { allowed: result.data!.allowed } as PermissionDecision,
        meta: {
          revision: result.meta.revision,
          requestId: result.meta.requestId,
          ...(result.meta.elapsedMs === undefined ? {} : { elapsedMs: result.meta.elapsedMs }),
          ...(result.meta.cacheStatus === undefined ? {} : { cacheStatus: result.meta.cacheStatus }),
        },
      } as ApiSuccess<T>;
    }

    if (url.pathname === "/list-relationships") {
      const removed = ["authorizationSubject", "authorizationSubjectType",
        "authorizationSubjectId", "permission", "authorization"];
      if (removed.some(key => Object.hasOwn(body, key))) {
        throw new ApiError(400, { error: { code: "validation-error",
          message: "Relationship reads do not accept authorization fields." } });
      }
      const result = await wire<WirePage<WireObject>>("reverse-relationships", {
        resourceType: identifier(body.resourceType),
        subjectType: nestedIdentifier(body, "subject", "type"),
        subjectId: nestedIdentifier(body, "subject", "id"),
        relation: identifier(body.relation),
        pageSize: number(body.pageSize, 20),
        cache: body.cache !== false,
        populateCache: body.populateCache !== false,
        ...(body.after ? { cursor: identifier(body.after) } : {}),
        ...consistencyInput,
      }, signal);
      const parent = object(body.subject as WireObject);
      const items: RelationshipPage["items"] = result.data!.items.map(item => ({
        subject: parent,
        relation: identifier(body.relation),
        resource: object(item),
      }));
      const page: RelationshipPage = {
        items,
        pageInfo: {
          hasNextPage: result.data!.pageInfo.hasNextPage,
          hasPreviousPage: Boolean(body.after),
          endCursor: result.data!.pageInfo.endCursor,
        },
      };
      return wireEnvelope(page, result) as ApiSuccess<T>;
    }

    throw new ApiError(404, { error: { code: "route-not-found", message: "The Explorer operation is not available." } });
  };

  return {
    dispatcher,
    release: async () => {
      await transport.release();
    },
  };
}

function presentBootstrap(descriptor: ProfileDescriptor, schema: SchemaInfo): Bootstrap {
  const supported = [...new Set(descriptor.capabilities.consistencyModes
    .map(presentConsistency)
    .filter((mode): mode is ConsistencyMode => mode !== null))];
  const defaultMode = supported[0] ?? "minimize-latency";
  const fullyConsistent = supported.includes("fully-consistent");
  return {
    status: "ready",
    seed: descriptor.localSeed ? presentLocalSeed(descriptor.localSeed.progress) : {
      status: "ready",
      serversAdded: 0,
      serversCompleted: 0,
      serversTarget: 0,
      totalServers: descriptor.dataset.serverCount,
    },
    totals: { servers: descriptor.dataset.serverCount, resources: descriptor.dataset.logicalResourceCount },
    ...(descriptor.localSeed ? { localSeed: descriptor.localSeed } : {}),
    schema,
    quickSubjects: [
      { id: "super-user", label: "Super user" },
      { id: "user-1", label: "User 1" },
      { id: "user-2", label: "User 2" },
    ],
    pageSizeOptions: [5, 10, 20, 25, 50, 100, 250, 500, 1000],
    defaultPageSize: 20,
    consistency: {
      default: defaultMode,
      supported,
      fullyConsistent,
      fullyConsistentReason: fullyConsistent
        ? "The immutable deployed database value is authoritative for this read-only profile."
        : fullyConsistentReason(descriptor),
      atExactSnapshotDateSelection: descriptor.capabilities.consistencyModes.includes("historical-date"),
      atExactSnapshotDateSelectionReason: "Historical date selection is not advertised by this profile.",
    },
    capabilities: { schemaWrite: false, seedWrite: descriptor.profile.backend === "datascript" && Boolean(descriptor.localSeed), cacheEvict: false },
  };
}

function fullyConsistentReason(descriptor: ProfileDescriptor): string {
  if (descriptor.profile.backend === "datascript") return "";
  if (descriptor.profile.backend === "datahike") {
    const storage = descriptor.profile.storage === "s3" ? "S3 GETs" : "DynamoDB reads";
    return `Fully consistent is disabled in this cost-controlled demo because refreshing Datahike for every query may issue ${storage}. Use Refresh Snapshot when you need a newer basis.`;
  }
  return "This read-only deployment does not advertise an authoritative-head synchronization operation.";
}

function presentSchema(schema: WireSchema): SchemaInfo {
  const resourceTypes = schema.types
    .filter((type) => type.permissions.length > 0)
    .map((type) => type.name)
    .sort();
  const permissionsByType = Object.fromEntries(schema.types.map((type) => [
    type.name,
    type.permissions.map((permission) => permission.name).sort(),
  ]));
  const childPaths: SchemaInfo["childPaths"] = {};
  for (const type of schema.types) {
    for (const relation of type.relations) {
      for (const subjectType of relation.subjectTypes) {
        (childPaths[subjectType] ??= []).push({ resourceType: type.name, relation: relation.name });
      }
    }
  }
  for (const paths of Object.values(childPaths)) {
    paths.sort((left, right) => `${left.resourceType}:${left.relation}`.localeCompare(`${right.resourceType}:${right.relation}`));
  }
  const source = schema.types.map((type) => [
    `definition ${type.name} {`,
    ...type.relations.map((relation) => `  relation ${relation.name}: ${relation.subjectTypes.join(" | ")}`),
    ...(type.relations.length && type.permissions.length ? [""] : []),
    ...type.permissions.map((permission) => `  permission ${permission.name} = ${permission.expression}`),
    "}",
  ].join("\n")).join("\n\n");
  return {
    source,
    resourceTypes,
    permissionsByType,
    childPaths,
    nodes: schema.types.map((type) => ({ id: type.name, permissions: permissionsByType[type.name] ?? [], permissionDefinitions: type.permissions })),
    links: schema.types.flatMap((type) => type.relations.flatMap((relation) =>
      relation.subjectTypes.map((subjectType) => ({ source: type.name, target: subjectType, label: relation.name })))),
    resourceCount: schema.types.length,
    relationCount: schema.types.reduce((total, type) => total + type.relations.length, 0),
    permissionCount: schema.types.reduce((total, type) => total + type.permissions.length, 0),
    presets: [{ id: schema.sha256, label: "Published", schema: source }],
  };
}

function presentMeta(
  profile: ExplorerProfile,
  requestId: string,
  basis: ProfileDescriptor["basis"] | null | undefined,
  elapsedMs?: number,
  cacheStatus?: ApiMeta["cacheStatus"],
): ApiMeta {
  const active = basis ?? {
    id: profile.deployment.deploymentId,
    capturedAt: profile.deployment.deployedAt,
    behavior: "fixed-environment",
    fixedForEnvironment: true,
  };
  return {
    revision: active.id,
    requestId,
    ...(elapsedMs === undefined ? {} : { elapsedMs }),
    ...(cacheStatus === undefined ? {} : { cacheStatus }),
    basis: {
      id: active.id,
      backend: profile.backend,
      sourceId: active.id,
      revision: numericRevision(active.id),
      exactLocator: active.fixedForEnvironment ? active.id : null,
      kind: active.behavior,
      capturedAt: active.capturedAt,
      sourceLifecycle: active.behavior,
    },
  };
}

function presentConsistency(value: string): ConsistencyMode | null {
  if (value === "minimize") return "minimize-latency";
  if (value === "at-least") return "at-least-as-fresh";
  if (value === "exact") return "at-exact-snapshot";
  if (value === "authoritative") return "fully-consistent";
  return null;
}

function consistencyMode(value: unknown): ConsistencyMode {
  if (value && typeof value === "object" && "mode" in value) {
    return identifier((value as { mode: unknown }).mode) as ConsistencyMode;
  }
  return "minimize-latency";
}

function freshnessFloor(value: unknown): {
  atLeastAsFreshAs?: string;
  atLeastAsFreshBasisId?: string;
  atLeastAsFreshBasisCapturedAt?: string;
} {
  if (!value || typeof value !== "object") return {};
  const candidate = value as {
    atLeastAsFreshAs?: unknown;
    atLeastAsFreshBasisId?: unknown;
    atLeastAsFreshBasisCapturedAt?: unknown;
  };
  const instant = candidate.atLeastAsFreshAs;
  const basisId = candidate.atLeastAsFreshBasisId;
  const basisCapturedAt = candidate.atLeastAsFreshBasisCapturedAt;
  return {
    ...(typeof instant === "string" && instant.length > 0
      ? { atLeastAsFreshAs: instant }
      : {}),
    ...(typeof basisId === "string" && basisId.length > 0
      ? { atLeastAsFreshBasisId: basisId }
      : {}),
    ...(typeof basisCapturedAt === "string" && basisCapturedAt.length > 0
      ? { atLeastAsFreshBasisCapturedAt: basisCapturedAt }
      : {}),
  };
}

function exactSnapshot(value: unknown): { atExactSnapshotAt?: string } {
  if (!value || typeof value !== "object") return {};
  const instant = (value as { atExactSnapshotAt?: unknown }).atExactSnapshotAt;
  return typeof instant === "string" && instant.length > 0
    ? { atExactSnapshotAt: instant }
    : {};
}

function preferredConsistency(
  descriptor: ProfileDescriptor,
  mode: ConsistencyMode,
  historicalDate = false,
): string {
  const requested = mode === "minimize-latency"
    ? "minimize"
    : mode === "at-least-as-fresh"
      ? "at-least"
      : mode === "at-exact-snapshot"
        ? historicalDate && descriptor.capabilities.consistencyModes.includes("historical-date")
          ? "historical-date"
          : "exact"
        : "authoritative";
  return descriptor.capabilities.consistencyModes.includes(requested)
    ? requested
    : descriptor.capabilities.consistencyModes[0];
}

function object(value: WireObject): EaclObject {
  return { type: value.type, id: value.id };
}

function objectPage(page: WirePage<WireObject>): ObjectPage {
  return {
    items: page.items.map(object),
    pageInfo: {
      hasNextPage: page.pageInfo.hasNextPage,
      hasPreviousPage: false,
      endCursor: page.pageInfo.endCursor,
    },
  };
}

function parseBody(value: BodyInit | null | undefined): Record<string, unknown> {
  if (value === undefined || value === null || value === "") return {};
  if (typeof value !== "string") throw new TypeError("Explorer request body must be JSON.");
  const parsed = JSON.parse(value) as unknown;
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new TypeError("Explorer request body must be an object.");
  return parsed as Record<string, unknown>;
}

function presentCacheSnapshot(value: Record<string, unknown>): CacheSnapshot {
  const provider = value.provider;
  const operations = value.operations;
  const capturedAt = value.capturedAt;
  if (provider && typeof provider === "object" && !Array.isArray(provider)
    && operations && typeof operations === "object" && !Array.isArray(operations)
    && typeof capturedAt === "string" && capturedAt.length > 0) {
    return {
      provider: provider as Record<string, unknown>,
      operations: operations as Record<string, unknown>,
      capturedAt,
    };
  }

  // During a rolling deployment the static client can briefly reach an older
  // profile version. Preserve its summary instead of failing the whole panel.
  return { provider: value, operations: {}, capturedAt: new Date().toISOString() };
}

function nestedIdentifier(value: Record<string, unknown>, key: string, child: string): string {
  const nested = value[key];
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) throw new TypeError(`Missing ${key}.`);
  return identifier((nested as Record<string, unknown>)[child]);
}

function identifier(value: unknown): string {
  if (typeof value !== "string" || !value) throw new TypeError("Explorer identifier is missing.");
  return value;
}

function number(value: unknown, fallback: number): number {
  return Number.isSafeInteger(value) && Number(value) > 0 ? Number(value) : fallback;
}

function positiveInteger(value: string | null, fallback: number): number {
  if (value === null) return fallback;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : fallback;
}

function numericRevision(value: string): number {
  const match = /(\d+)(?!.*\d)/u.exec(value);
  return match ? Number(match[1]) : 0;
}
